import { useCallback, useMemo, useState } from 'react';

import { PaginationState } from '@tanstack/react-table';

interface UseTablePaginationProps {
  manualPagination: boolean;
  controlledPageIndex: number;
  rowsPerPage: number;
  onPageChange?: (newPageIndex: number) => void;
}

interface UseTablePaginationReturn {
  paginationState: PaginationState;
  handlePaginationChange: (
    updaterOrValue: PaginationState | ((old: PaginationState) => PaginationState)
  ) => void;
}

/**
 * Hook that manages table pagination state and handlers.
 *
 * Supports both controlled (manual/server-side) and uncontrolled (client-side)
 * pagination modes. In manual mode, pagination changes are delegated to the
 * parent via `onPageChange`. In automatic mode, state is managed internally.
 *
 * @param props - The hook configuration.
 * @param props.manualPagination - Whether pagination is server-side controlled.
 * @param props.controlledPageIndex - Current page index for controlled mode.
 * @param props.rowsPerPage - Number of rows per page.
 * @param props.onPageChange - Callback for page changes in controlled mode.
 * @returns Object containing pagination state and change handler.
 *
 * @example
 * ```tsx
 * // Client-side pagination
 * const { paginationState, handlePaginationChange } = useTablePagination({
 *   manualPagination: false,
 *   controlledPageIndex: 0,
 *   rowsPerPage: 10
 * });
 *
 * // Server-side pagination
 * const { paginationState, handlePaginationChange } = useTablePagination({
 *   manualPagination: true,
 *   controlledPageIndex: currentPage,
 *   rowsPerPage: 10,
 *   onPageChange: (page) => fetchPage(page)
 * });
 * ```
 */
export const useTablePagination = ({
  manualPagination,
  controlledPageIndex,
  rowsPerPage,
  onPageChange
}: UseTablePaginationProps): UseTablePaginationReturn => {
  const [localPagination, setLocalPagination] = useState<PaginationState>({
    pageIndex: controlledPageIndex ?? 0,
    pageSize: rowsPerPage
  });

  const handlePaginationChange = useCallback(
    (
      updaterOrValue:
        | PaginationState
        | ((old: PaginationState) => PaginationState)
    ): void => {
      let newState: PaginationState;

      if (typeof updaterOrValue === 'function') {
        newState = updaterOrValue(localPagination);
      } else {
        newState = updaterOrValue;
      }

      if (manualPagination) {
        if (onPageChange) {
          onPageChange(newState.pageIndex);
        }
      } else {
        setLocalPagination(newState);
      }
    },
    [localPagination, manualPagination, onPageChange]
  );

  const paginationState = useMemo((): PaginationState => {
    if (manualPagination) {
      return { pageIndex: controlledPageIndex, pageSize: rowsPerPage };
    }
    return localPagination;
  }, [manualPagination, controlledPageIndex, rowsPerPage, localPagination]);

  return {
    paginationState,
    handlePaginationChange
  };
};
